import React, { useEffect, useMemo, useState } from 'react';
import { header } from '../components/header.encry';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL || 'https://api.pegasus.le-celadon.ma';

const formatDateInput = (date) => {
  if (!date) {
    return '';
  }
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (!value) {
    return 0;
  }
  const normalized = String(value).replace(',', '.');
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const calculateOrderTotal = (order) => {
  if (!order) {
    return 0;
  }
  if (order.source === 'dolibarr_pos') {
    return parseNumber(order.totalPrice);
  }
  const products = Array.isArray(order.products) ? order.products : [];
  const productsTotal = products.reduce((sum, product) => {
    const quantity = parseNumber(product?.quantity || 1) || 1;
    const productPrice = parseNumber(product?.price);
    const optionsTotal = Array.isArray(product?.options)
      ? product.options.reduce((optionSum, option) => {
          const optionQuantity = parseNumber(option?.quantity || 1) || 1;
          const optionPrice = parseNumber(option?.price);
          return optionSum + optionQuantity * optionPrice;
        }, 0)
      : 0;
    return sum + quantity * productPrice + optionsTotal;
  }, 0);

  if (productsTotal > 0) {
    return productsTotal;
  }
  return parseNumber(order.totalPrice);
};

const normalizeOrdersResponse = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && Array.isArray(payload.orders)) {
    return payload.orders;
  }
  if (payload && Array.isArray(payload.data)) {
    return payload.data;
  }
  if (payload && Array.isArray(payload.response)) {
    return payload.response;
  }
  return [];
};

const currencyFormatter = new Intl.NumberFormat('fr-MA', {
  style: 'currency',
  currency: 'MAD'
});

const AdminOrdersSummary = () => {
  const [selectedDate, setSelectedDate] = useState(() => formatDateInput(new Date()));
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dolibarrWarning, setDolibarrWarning] = useState('');
  const [modalGroup, setModalGroup] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const roles = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('roles') || '[]');
    } catch (err) {
      return [];
    }
  }, []);
  const isAdmin = roles.includes('admin');

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    const controller = new AbortController();
    const fetchOrders = async () => {
      setIsLoading(true);
      setError('');
      setDolibarrWarning('');
      try {
        const dateQuery = selectedDate ? `?date=${selectedDate}` : '';
        const requestOptions = { ...header(), signal: controller.signal };
        const [pegasusResult, dolibarrResult] = await Promise.allSettled([
          fetch(`${API_URL}/orders/by-date${dateQuery}`, requestOptions),
          fetch(`${API_URL}/dolibarr/pos-orders/by-companies${dateQuery}`, requestOptions)
        ]);

        if (pegasusResult.status === 'rejected' || !pegasusResult.value.ok) {
          throw new Error('Failed to fetch orders');
        }
        const payload = await pegasusResult.value.json();
        const normalizedPegasusOrders = normalizeOrdersResponse(payload);

        let normalizedDolibarrOrders = [];
        if (dolibarrResult.status === 'fulfilled' && dolibarrResult.value.ok) {
          const dolibarrPayload = await dolibarrResult.value.json();
          const companies = Array.isArray(dolibarrPayload?.companies) ? dolibarrPayload.companies : [];
          normalizedDolibarrOrders = companies.flatMap((company) => (
            Array.isArray(company?.orders) ? company.orders : []
          ));
          const failedCompanies = companies.filter((company) => company?.status === 'error');
          if (failedCompanies.length > 0) {
            setDolibarrWarning(
              `Les ventes POS Dolibarr de ${failedCompanies.map((company) => company.company_name).join(', ')} n'ont pas pu être chargées.`
            );
          }
        } else {
          setDolibarrWarning("Les ventes POS Dolibarr n'ont pas pu être chargées.");
        }

        setOrders([...normalizedPegasusOrders, ...normalizedDolibarrOrders]);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch orders by date', err);
          setError("Impossible de récupérer les commandes. Veuillez réessayer.");
          setOrders([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrders();
    return () => controller.abort();
  }, [selectedDate, isAdmin, refreshKey]);

  useEffect(() => {
    if (modalGroup) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [modalGroup]);

  const aggregatedOrders = useMemo(() => {
    if (!Array.isArray(orders) || orders.length === 0) {
      return [];
    }
    const aggregationMap = orders.reduce((acc, order) => {
      const productType = order?.productType || 'Inconnu';
      const total = calculateOrderTotal(order);
      if (!acc[productType]) {
        acc[productType] = {
          productType,
          totalAmount: 0,
          ordersCount: 0,
          orders: []
        };
      }
      acc[productType].totalAmount += total;
      acc[productType].ordersCount += 1;
      acc[productType].orders.push({
        ...order,
        computedTotal: total
      });
      return acc;
    }, {});

    return Object.values(aggregationMap).sort((a, b) => a.productType.localeCompare(b.productType));
  }, [orders]);

  const overallTotals = useMemo(() => {
    return aggregatedOrders.reduce(
      (acc, group) => {
        acc.amount += group.totalAmount;
        acc.count += group.ordersCount;
        return acc;
      },
      { amount: 0, count: 0 }
    );
  }, [aggregatedOrders]);

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const closeModal = () => setModalGroup(null);

  if (!isAdmin) {
    return (
      <div className="alert alert-danger" role="alert">
        Cette page est réservée aux administrateurs.
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
        <h3 className="card-title mb-0">Commandes par canal</h3>
        <div className="d-flex gap-2 align-items-center">
          <label htmlFor="orders-date" className="me-2 mb-0 fw-semibold">Date</label>
          <input
            id="orders-date"
            type="date"
            className="form-control"
            value={selectedDate}
            onChange={handleDateChange}
            max={formatDateInput(new Date())}
          />
          <button type="button" className="btn btn-outline-primary" onClick={handleRefresh} disabled={isLoading}>
            <i className="fa-solid fa-arrows-rotate"></i> Actualiser
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {dolibarrWarning && !error && (
          <div className="alert alert-warning" role="alert">
            {dolibarrWarning}
          </div>
        )}
        <div className="table-responsive">
          <table className="table table-striped align-middle">
            <thead>
              <tr>
                <th>Canal</th>
                <th className="text-center">Nombre de commandes</th>
                <th className="text-end">Total</th>
                <th className="text-center">Détails</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={4} className="text-center">Chargement des commandes...</td>
                </tr>
              )}
              {!isLoading && aggregatedOrders.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center">Aucune commande trouvée pour cette date.</td>
                </tr>
              )}
              {!isLoading && aggregatedOrders.map((group) => (
                <tr key={group.productType}>
                  <td className="fw-semibold text-capitalize">{group.productType}</td>
                  <td className="text-center">{group.ordersCount}</td>
                  <td className="text-end">{currencyFormatter.format(group.totalAmount)}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setModalGroup(group)}
                    >
                      Voir les commandes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <h5>Totaux globaux</h5>
          <div className="table-responsive">
            <table className="table table-bordered mb-0">
              <thead>
                <tr>
                  <th>Nombre total de commandes</th>
                  <th>Total cumulé</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{overallTotals.count}</td>
                  <td>{currencyFormatter.format(overallTotals.amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {modalGroup && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Commandes {modalGroup.productType}</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  {modalGroup.orders.map((order) => (
                    <div key={order._id?.$oid || order._id || order.orderCode || order.orderNumber} className="mb-4 pb-3 border-bottom">
                      <div className="d-flex justify-content-between flex-wrap">
                        <div>
                          <h6 className="mb-1">Commande {order.orderNumber || order.orderCode}</h6>
                          {order.CustomerName && <div className="text-muted">Client : {order.CustomerName}</div>}
                          <div className="text-muted">{order.orderDate} {order.orderTime}</div>
                          {order.orderStatus && (
                            <span className="badge bg-secondary mt-2 text-uppercase">{order.orderStatus}</span>
                          )}
                        </div>
                        <div className="text-end fw-semibold">
                          Total : {currencyFormatter.format(order.computedTotal)}
                        </div>
                      </div>
                      <div className="mt-3">
                        <h6>Produits</h6>
                        <ul className="list-unstyled mb-0">
                          {(order.products || []).map((product, idx) => (
                            <li key={`${order.orderCode || order.orderNumber}-${idx}`} className="mb-2">
                              <div className="fw-semibold">{product.title}</div>
                              <div className="text-muted small">Quantité : {product.quantity} — Prix : {currencyFormatter.format(parseNumber(product.price))}</div>
                              {Array.isArray(product.options) && product.options.length > 0 && (
                                <ul className="mt-2 ms-3">
                                  {product.options.map((option, optionIdx) => (
                                    <li key={`${order.orderCode || order.orderNumber}-${idx}-${optionIdx}`} className="text-muted">
                                      {option.title} {option.quantity ? `(x${option.quantity})` : ''}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default AdminOrdersSummary;
